import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {
    NatResidentWindowFilmAttachInfo,
    NatResidentWindowFilmAttachLocationState,
} from './NatResidentApplicationInterface'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import NatResidentWindowFilmAttachForm from './resident/NatResidentWindowFilmAttachForm'
import {FileInformation} from '../../../../components/applications/ResidentAttachmentFileArea'
import {NatPutWindowFilmAttachRequest, NatPutWindowFilmAttachResponse} from '../repository/NatPutWindowFilmAttach'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {NatPostFileRequest, NatPostFileResponse} from '../../files/repository/NatPostFile'
import {MESSAGE_TYPE} from '../../../../CodeMaster'
import {NatFileRepo} from '../../files/repository/NatFileRepo'
import {FILE_EXTENSION} from '../../../common/File/NatFileHelper'
import {HKConstants} from '../../../../HKConstants'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    fileRepo: NatFileRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentWindowFilmAttachLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    windowFilmAttachDate: Nullable<string>
    contractorRequest: Nullable<string>
    contractorName: Nullable<string>
    contractorZipCode: Nullable<string>
    contractorPrefecture: Nullable<string>
    contractorAddress2: Nullable<string>
    contractorAddress3: Nullable<string>
    contractorBuildingRoomNumber: Nullable<string>
    contractorPhoneNumber: Nullable<string>
    contractorRep: Nullable<string>
    applyToAttachment: Nullable<FileInformation[]>
    isLoadCompleted: boolean
}

class NatResidentWindowFilmAttachInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            windowFilmAttachDate: null,
            contractorRequest: null,
            contractorName: null,
            contractorZipCode: null,
            contractorPrefecture: null,
            contractorAddress2: null,
            contractorAddress3: null,
            contractorBuildingRoomNumber: null,
            contractorPhoneNumber: null,
            contractorRep: null,
            applyToAttachment: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {applicationRepo, eventEmitter, condominiumCd} = this.props
        const {applyInfo, applyId} = this.props.location.state

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultWindowFilmAttachDate = applyInfo?.windowFilmAttachDate ?? null
        let defaultContractorRequest = applyInfo?.contractorRequest ?? null
        let defaultContractorName = applyInfo?.contractorName ?? null
        let defaultContractorZipCode = applyInfo?.contractorZipCode ?? null
        let defaultContractorPrefecture = applyInfo?.contractorPrefecture ?? null
        let defaultContractorAddress2 = applyInfo?.contractorAddress2 ?? null
        let defaultContractorAddress3 = applyInfo?.contractorAddress3 ?? null
        let defaultContractorBuildingRoomNumber = applyInfo?.contractorBuildingRoomNumber ?? null
        let defaultContractorPhoneNumber = applyInfo?.contractorPhoneNumber ?? null
        let defaultContractorRep = applyInfo?.contractorRep ?? null
        let defaultApplyToAttachment = applyInfo?.applyToAttachment ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const windowFilmAttachesDetail =
                    await this.props.asyncCallFunction(applicationRepo.getWindowFilmAttachApplyForResident.bind(
                            applicationRepo, condominiumCd, applyId),
                        (httpErrorOrNetworkError) => {
                            eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                        },
                    )
                if (windowFilmAttachesDetail?.details) {
                    defaultWindowFilmAttachDate = windowFilmAttachesDetail.details.windowFilmAttachDate
                    defaultContractorRequest = windowFilmAttachesDetail.details.contractorRequest
                    defaultContractorName = windowFilmAttachesDetail.details.contractorName
                    defaultContractorZipCode = windowFilmAttachesDetail.details.contractorZipCode
                    defaultContractorPrefecture = windowFilmAttachesDetail.details.contractorPrefecture
                    defaultContractorAddress2 = windowFilmAttachesDetail.details.contractorAddress2
                    defaultContractorAddress3 = windowFilmAttachesDetail.details.contractorAddress3
                    defaultContractorBuildingRoomNumber = windowFilmAttachesDetail.details.contractorBuildingRoomNumber
                    defaultContractorPhoneNumber = windowFilmAttachesDetail.details.contractorPhoneNumber
                    defaultContractorRep = windowFilmAttachesDetail.details.contractorRep
                    defaultApplyToAttachment =
                        windowFilmAttachesDetail.details.applyToAttachment.map(attachment => {
                            return {
                                fileName: attachment.fileName,
                                fileData: null,
                                fileIndex: attachment.seq,
                                fileNumber: attachment.fileNumber,
                            }
                        })
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }

        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            windowFilmAttachDate: defaultWindowFilmAttachDate,
            contractorRequest: defaultContractorRequest,
            contractorName: defaultContractorName,
            contractorZipCode: defaultContractorZipCode,
            contractorPrefecture: defaultContractorPrefecture,
            contractorAddress2: defaultContractorAddress2,
            contractorAddress3: defaultContractorAddress3,
            contractorBuildingRoomNumber: defaultContractorBuildingRoomNumber,
            contractorPhoneNumber: defaultContractorPhoneNumber,
            contractorRep: defaultContractorRep,
            applyToAttachment: defaultApplyToAttachment,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(
        applyContent: NatResidentWindowFilmAttachInfo,
    ): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {

            const copyFileNumbers: number[] = applyContent.applyToAttachment
                .map(attachment => attachment.fileNumber)
                .filter((fileNumber): fileNumber is Exclude<typeof fileNumber, undefined> => fileNumber !== undefined)
                .filter((fileNumber): fileNumber is Exclude<typeof fileNumber, null> => fileNumber !== null)

            const applyRequest: NatPutWindowFilmAttachRequest = {
                applicantFamilyName: applyContent.applicantFamilyName,
                applicantFirstName: applyContent.applicantFirstName,
                windowFilmAttachDate: applyContent.windowFilmAttachDate,
                contractorRequest: applyContent.contractorRequest,
                contractorName: applyContent.contractorName || null,
                contractorZipCode: applyContent.contractorZipCode || null,
                contractorPrefecture: applyContent.contractorPrefecture || null,
                contractorAddress2: applyContent.contractorAddress2 || null,
                contractorAddress3: applyContent.contractorAddress3 || null,
                contractorBuildingRoomNumber: applyContent.contractorBuildingRoomNumber || null,
                contractorPhoneNumber: applyContent.contractorPhoneNumber || null,
                contractorRep: applyContent.contractorRep || null,
                reApplyCopyFileNumbers: copyFileNumbers || null,
                confirmed: false,
            }

            const extensions = [
                ...FILE_EXTENSION.JPEG,
                ...FILE_EXTENSION.PNG,
                ...FILE_EXTENSION.PDF,
            ]
            const fileRequests: NatPostFileRequest[] = applyContent.applyToAttachment
                .filter(attachmentFile => attachmentFile.fileNumber === null)
                .map((attachmentFile) => {
                    return ({
                        fileName: attachmentFile.fileName,
                        fileData: attachmentFile.fileData ?? null,
                        fileIndex: attachmentFile.fileIndex,
                        fileNumber: attachmentFile.fileNumber ?? null,
                        maxSizeMB: HKConstants.CMN.OPC.UPLOAD_FILE_LIMIT_MEGABYTES,
                        extension: extensions,
                    })
                })

            const applySuccess = await this.applyWindowFilmAttach(applyRequest)
            if (!applySuccess) {
                return
            }

            const fileSuccess = await this.fileUpload(fileRequests)
            if (!fileSuccess) {
                return
            }

            const locationState: NatResidentWindowFilmAttachLocationState = {
                applyInfo: {
                    applicantFamilyName: applyRequest.applicantFamilyName,
                    applicantFirstName: applyRequest.applicantFirstName,
                    windowFilmAttachDate: applyRequest.windowFilmAttachDate,
                    contractorRequest: applyRequest.contractorRequest,
                    contractorName: applyRequest.contractorName,
                    contractorZipCode: applyRequest.contractorZipCode,
                    contractorPrefecture: applyRequest.contractorPrefecture,
                    contractorAddress2: applyRequest.contractorAddress2,
                    contractorAddress3: applyRequest.contractorAddress3,
                    contractorBuildingRoomNumber: applyRequest.contractorBuildingRoomNumber,
                    contractorPhoneNumber: applyRequest.contractorPhoneNumber,
                    contractorRep: applyRequest.contractorRep,
                    applyToAttachment: applyContent.applyToAttachment,
                },
                applyId: this.props.location.state.applyId,
            }
            this.props.navigate(
                `/resident/condominiums/${this.props.condominiumCd}/applications/windowFilmAttaches/confirm`,
                {state: JSON.parse(JSON.stringify(locationState))},
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async applyWindowFilmAttach(request: NatPutWindowFilmAttachRequest): Promise<boolean> {
        const {applicationRepo, condominiumCd} = this.props

        const response: Nullable<NatPutWindowFilmAttachResponse> = await this.props.asyncCallFunction(
            applicationRepo.applyWindowFilmAttach.bind(
                applicationRepo, condominiumCd, request),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
            },
            {
                defaultErrorTitle: Messages.EOPCR0014.toString(),
                defaultErrorTitlePriority: true,
                replacementDetailErrorMessages: [{
                    targetMessageId: Messages.EOPCA0004,
                    replacementMessageId: Messages.EOPCR0015,
                }],
            },
        )
        return !!(response && response.requestError === null)
    }

    async fileUpload(requests: NatPostFileRequest[]): Promise<boolean> {
        const {fileRepo} = this.props

        let reportIndex: number = 1
        const fileCount: number = requests.length
        const responses: Nullable<NatPostFileResponse>[] = []

        for (const request of requests) {
            this.props.eventEmitter.changeLoadingMessage(`ファイルアップロード中... ${reportIndex}/${fileCount}`)

            const fileResponse = await this.props.asyncCallFunction(
                fileRepo.fileUpload.bind(
                    fileRepo, request),
                async (httpErrorOrNetworkError) => {
                    const requestItemErrors = httpErrorOrNetworkError.responseBody.requestItemErrors
                    requestItemErrors.forEach((item) => {
                        this.props.eventEmitter.emit(item.resultCode, item)
                    })
                },
            )
            reportIndex = reportIndex + 1
            responses.push(fileResponse)
        }

        if (responses.filter(file => file && file.requestItemErrors === null).length === fileCount) {
            return true
        } else {
            await this.props.eventEmitter.showHeadingAreaMessage({
                title: Messages.EOPCR0014.toString(),
                messageLevel: MESSAGE_TYPE.INFORMATION,
                detail: Messages.EOPCA0005.toString(),
            })
            return false
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.location.state.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/windowFilmAttaches/${this.props.location.state.applyId}/copy`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications/windowFilmAttaches/agreement`
                    }>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    窓ガラスフィルム貼付申請
                </PageTitle>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentWindowFilmAttachForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentWindowFilmAttachInputPage)
